import { useCallback, useEffect, useRef, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CatalogCategoryKindType, DirCatalogCategoryType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import * as apiSettings from 'api/apiSettings';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import { CATALOG_DIR_I18N_NS, catalogDirApi } from '../model/catalogDirApi';
import AddDirFormik from './AddDirFormik';
import DirsList from './DirsList';

interface OffCatalogDirsProps {
    kind: CatalogCategoryKindType | null;
    setKind: (kind: CatalogCategoryKindType | null) => void;
}

interface DirItem {
    ID: number;
    name: string;
}

const DIRS_KEY: Record<CatalogCategoryKindType, 'ProductCategory' | 'ProductSetCategory' | 'ServiceCategory'> = {
    product: 'ProductCategory',
    set: 'ProductSetCategory',
    service: 'ServiceCategory',
};

const extractItems = (kind: CatalogCategoryKindType, settings: { DIRS?: Record<string, DirCatalogCategoryType[] | undefined> } | null): DirItem[] => {
    return (settings?.DIRS?.[DIRS_KEY[kind]] || [])
        .filter((item) => !item.hidden && item.ID)
        .map((item) => ({ ID: item.ID, name: String(item.categoryName || '') }));
};

const OffCatalogDirs = ({ kind, setKind }: OffCatalogDirsProps) => {
    const ns = kind ? CATALOG_DIR_I18N_NS[kind] : 'Products';
    const { t } = useTranslation([ns, 'common']);
    const { showToast } = useToast();
    const { dispatchLocalSettings } = useSettings();
    const showToastRef = useRef(showToast);
    const tRef = useRef(t);
    showToastRef.current = showToast;
    tRef.current = t;

    const [items, setItems] = useState<DirItem[]>([]);
    const [usage, setUsage] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(false);

    const applyData = useCallback(
        (
            kindValue: CatalogCategoryKindType,
            resSetting: NonNullable<Awaited<ReturnType<typeof apiSettings.getSettings>>>,
            resUsage: Record<number, number>,
        ) => {
            dispatchLocalSettings({ type: 'SET', settings: resSetting });
            setItems(extractItems(kindValue, resSetting));
            setUsage(resUsage);
        },
        [dispatchLocalSettings],
    );

    const fetchData = useCallback(async (kindValue: CatalogCategoryKindType) => {
        const [resSetting, resUsage] = await Promise.all([
            apiSettings.getSettings(),
            catalogDirApi[kindValue].getUsage(),
        ]);
        if (!resSetting || resUsage === null) return null;
        return { resSetting, resUsage };
    }, []);

    const syncData = useCallback(async (): Promise<void> => {
        if (!kind) return;
        const data = await fetchData(kind);
        if (!data) {
            showToastRef.current(tRef.current('common:messError'), 'danger');
            return;
        }
        applyData(kind, data.resSetting, data.resUsage);
    }, [applyData, fetchData, kind]);

    useEffect(() => {
        if (!kind) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            const data = await fetchData(kind);
            if (cancelled) return;
            setLoading(false);
            if (!data) {
                showToastRef.current(tRef.current('common:messError'), 'danger');
                return;
            }
            applyData(kind, data.resSetting, data.resUsage);
        })();

        return () => {
            cancelled = true;
        };
    }, [kind, applyData, fetchData]);

    const handleClose = () => setKind(null);

    return (
        <Offcanvas show={Boolean(kind)} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('dirs.categoriesTitle')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-coming-form d-flex flex-column h-100">
                    {kind ? <AddDirFormik kind={kind} onCreated={syncData} /> : null}
                    <div className="off-coming-content">
                        {loading ? <ESpinner /> : kind ? <DirsList kind={kind} items={items} usage={usage} onChanged={syncData} /> : null}
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffCatalogDirs;
